import React, { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Empty, Modal, Select, Steps } from "antd";
import { documentApis } from "../../../api/Documents";
import { useWbsName } from "../shared/useWbsCodes";
import { AddAccess } from "../../../components/accessControl/AccessControl";
import ProjectPageShell from "../shared/ProjectPageShell";
import TaskCreation, { TaskCreationPageApi } from "./TaskCreation";
import "./TaskCreatePage.css";

interface SelectedActivity {
  activityId: number;
  activityName: string;
  phaseId: number;
  phaseName: string;
}

interface ActivityOption extends SelectedActivity {
  value: string;
  label: string;
}

export const TASK_CREATE_STEPS = [
  { title: "Activity & Basics" },
  { title: "Assignment & Schedule" },
  { title: "Materials & Costing" },
  { title: "Extras & Review" },
];

// Full-page 4-step create-task flow. Replaces the old Select-Activity modal +
// 960px drawer: the activity is picked inline on step 1 and the shared
// TaskCreation form renders in "page" mode underneath. Changing the activity
// remounts TaskCreation (key={activityId}) so its one-shot data loading runs
// against the new activity; step-1 basics are carried across the remount.
const TaskCreatePage: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { currentProjectId, currentProjectName } = useSelector(
    (state: any) => state.layout
  );
  const locationState = (location.state as any) || {};
  const projectId = Number(
    params.projectId || locationState.id || currentProjectId || 0
  );
  const projectName =
    locationState.projectName || currentProjectName || "Project";

  const [activityOptions, setActivityOptions] = useState<ActivityOption[]>([]);
  const wbsName = useWbsName();
  const [selectedActivity, setSelectedActivity] =
    useState<SelectedActivity | null>(locationState.preselectedActivity || null);
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [validatingNext, setValidatingNext] = useState(false);
  // Step-1 values survive an activity switch (the form itself remounts).
  const [carriedBasics, setCarriedBasics] = useState<Record<string, any>>();
  const formApiRef = useRef<TaskCreationPageApi | null>(null);

  useEffect(() => {
    if (!projectId) {
      navigate("/pms/projects");
    }
  }, [projectId, navigate]);

  useEffect(() => {
    if (projectId) {
      documentApis.fetchActivityDropDown({ projectId }).then((res: any) => {
        const activities = res?.data ?? res ?? [];
        if (Array.isArray(activities)) {
          setActivityOptions(
            activities.map((item: any) => ({
              label: item.phaseName
                ? `${item.activityName} (${item.phaseName})`
                : item.activityName,
              value: String(item.activityId),
              activityId: Number(item.activityId),
              activityName: item.activityName,
              phaseId: item.phaseId,
              phaseName: item.phaseName,
            }))
          );
        }
      });
    }
  }, [projectId]);

  const goToTaskList = () => {
    navigate(`/pms/project/tasks/${projectId}`, {
      state: { id: projectId, projectName },
    });
  };

  const applyActivity = (opt: ActivityOption) => {
    setSelectedActivity({
      activityId: opt.activityId,
      activityName: opt.activityName,
      phaseId: opt.phaseId,
      phaseName: opt.phaseName,
    });
  };

  const handleActivityChange = (value: string | undefined) => {
    if (!value) return;
    const opt = activityOptions.find((o) => o.value === value);
    if (!opt || opt.activityId === selectedActivity?.activityId) return;
    if (!selectedActivity) {
      applyActivity(opt);
      return;
    }
    Modal.confirm({
      title: "Change activity?",
      content:
        "Changing the activity reloads its budget, materials, resources and dates. Your Basics are kept; assignment, schedule and costing entries will be reset.",
      okText: "Change Activity",
      cancelText: "Keep Current",
      onOk: () => {
        setCarriedBasics(formApiRef.current?.getBasics());
        applyActivity(opt);
      },
    });
  };

  // The LayoutProject Content pane is the scroll container (not window).
  const scrollToTop = () => {
    document
      .querySelector(".task-create-steps")
      ?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  const handleNext = async () => {
    if (!formApiRef.current) return;
    setValidatingNext(true);
    try {
      const ok = await formApiRef.current.validateStep(activeStep);
      if (ok) {
        setActiveStep((s) => Math.min(s + 1, TASK_CREATE_STEPS.length - 1));
        scrollToTop();
      } else {
        // Bring the first offending field into view.
        setTimeout(() => {
          document
            .querySelector(".invalid, .ant-form-item-has-error")
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    } finally {
      setValidatingNext(false);
    }
  };

  const isLastStep = activeStep === TASK_CREATE_STEPS.length - 1;

  const stepsBar = (
    <div className="task-create-steps">
      <Steps
        size="small"
        current={activeStep}
        items={TASK_CREATE_STEPS.map((s, i) => ({
          title: s.title,
          // Completed steps are clickable to go back; forward only via Next.
          disabled: i >= activeStep,
        }))}
        onChange={(step) => {
          if (step < activeStep) setActiveStep(step);
        }}
      />
    </div>
  );

  const noAccessFallback = (
    <div className="pms-hub-surface" style={{ padding: "64px 0" }}>
      <Empty description="You don't have permission to create tasks" />
    </div>
  );

  return (
    <>
      <ProjectPageShell
        headTitle="New Task"
        breadcrumb={[
          { label: projectName },
          { label: "Tasks", onClick: goToTaskList },
          { label: "New Task" },
        ]}
        eyebrow="Tasks"
        title="New Task"
        onBack={goToTaskList}
        toolbar={stepsBar}
        stickyToolbar
      >
        <AddAccess moduleName="Tasks" fallback={noAccessFallback}>
          <div className="task-create-card">
            {activeStep === 0 && (
              <div className="task-create-activity">
                <label className="form-label">
                  Activity <span style={{ color: "#e85347" }}>*</span>
                </label>
                <Select
                  placeholder="Search and select an activity"
                  style={{ width: "100%", maxWidth: 480 }}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    ((option?.label as string) ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={activityOptions.map((o) => ({
                    label: o.phaseName
                      ? `${wbsName(o.activityName)} (${wbsName(o.phaseName)})`
                      : wbsName(o.activityName),
                    value: o.value,
                  }))}
                  value={
                    selectedActivity
                      ? String(selectedActivity.activityId)
                      : undefined
                  }
                  onChange={handleActivityChange}
                />
                {selectedActivity?.phaseName && (
                  <div className="task-create-activity-phase">
                    Phase: {wbsName(selectedActivity.phaseName)}
                  </div>
                )}
              </div>
            )}

            {selectedActivity ? (
              <TaskCreation
                key={selectedActivity.activityId}
                refreshData={() => {}}
                mode="page"
                activeStep={activeStep}
                pageContext={{
                  projectId,
                  projectName,
                  phaseId: selectedActivity.phaseId,
                  phaseName: selectedActivity.phaseName,
                  activityId: selectedActivity.activityId,
                  activityName: selectedActivity.activityName,
                }}
                initialBasics={carriedBasics}
                apiRef={formApiRef}
                onCreated={goToTaskList}
                onCancel={goToTaskList}
                onSubmittingChange={setSubmitting}
              />
            ) : (
              <div style={{ padding: "48px 0", textAlign: "center" }}>
                <Empty description="Select an activity to start creating the task" />
              </div>
            )}
          </div>

          <div className="task-create-footer">
            <Button type="text" className="task-create-cancel" onClick={goToTaskList}>
              Cancel
            </Button>
            <div className="task-create-footer-nav">
              {activeStep > 0 && (
                <Button
                  onClick={() => {
                    setActiveStep((s) => s - 1);
                    scrollToTop();
                  }}
                  disabled={submitting}
                >
                  Back
                </Button>
              )}
              {isLastStep ? (
                <Button
                  type="primary"
                  loading={submitting}
                  disabled={!selectedActivity}
                  onClick={() => formApiRef.current?.submit()}
                >
                  Create Task
                </Button>
              ) : (
                <Button
                  type="primary"
                  loading={validatingNext}
                  disabled={!selectedActivity}
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </AddAccess>
      </ProjectPageShell>
    </>
  );
};

export default TaskCreatePage;
